import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Stack, Toolbar } from '@mui/material';
import { LoginNavbar } from '@/components/Navbar/LoginNavbar';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import ForgotPassword from './ForgotPassword';
import { useAuthStore } from '@/stores/authStore';
import { RouteMap } from '@/routes/Routes';
import { getRedirectResult, getAdditionalUserInfo } from 'firebase/auth';
import { auth } from '@/firebase/firebase';
import { useExtensionAuthSync } from '@/hooks/useExtensionAuthSync';
import { doSignOut, trackDubLead } from '@/firebase/auth';
import { errorReporter } from '@/services/errorReportingService';
import { isUserNotFoundError } from '@/utils/apiErrorMessage';
import { logger } from '@/services/loggerService';
import { isRecentLogout, isLogoutInProgress, clearLogoutFlag, setLogoutFlag } from '@/utils/crossTabAuthSync';
import { DEFAULT_FREE_SUBSCRIPTION } from '@/constants/subscriptionConstants';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');
  const noteId = searchParams.get('noteId');

  const { userLoggedIn, loading } = useAuthStore();
  const [showLogin, setShowLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [skipExtensionAuth, setSkipExtensionAuth] = useState(false);

  // Extension auth sync - silent auto-login when extension has auth
  const { extensionHasAuth, signInWithExtension } = useExtensionAuthSync();
  const silentLoginAttemptedRef = useRef(false);

  // Check if user was redirected from a shared note
  const redirectedFromSharedNote = sessionStorage.getItem('redirectAfterLogin')?.startsWith('/shared/');

  // Handle logout request from Chrome extension (via ?logout=true param)
  useEffect(() => {
    const logoutParam = searchParams.get('logout');
    if (logoutParam === 'true') {
      // Set flag in localStorage for cross-tab synchronization
      setLogoutFlag();
      // Clear the param from URL to prevent logout loop on refresh
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Sign out of Firebase
      void doSignOut().catch((error) => {
        errorReporter.critical('Firebase signOut failed from Login logout param', error, {
          feature: 'auth',
          operation: 'signOutFromLogoutParam',
        });
      });
      // Skip extension auth to prevent auto-login
      setSkipExtensionAuth(true);
    }
  }, [searchParams]);

  // Check for recent logout flag on mount (cross-tab sync)
  useEffect(() => {
    if (isRecentLogout()) {
      // Skip extension auth to prevent auto-login after logout
      setSkipExtensionAuth(true);
    }
  }, []);
  // Handle redirect result from Google sign-in
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          // User successfully signed in via redirect
          // User signed in via redirect successfully

          // CONTRACT #6: fire Dub tracking FIRST, using Firebase's authoritative
          // isNewUser signal, before any backend call. This is the popup-blocked /
          // COOP fallback path (mobile Safari, LinkedIn/Instagram/Slack in-app
          // browsers) — without this, referral attribution is lost for all of them.
          const additionalInfo = getAdditionalUserInfo(result);
          if (additionalInfo?.isNewUser) {
            trackDubLead({
              customerExternalId: result.user.uid,
              customerEmail: result.user.email || undefined,
              customerName: result.user.displayName || undefined,
              signupMethod: 'google',
            });
          }

          // Handle the token storage and user creation
          const { handleUserTokenStorage } = await import('@/firebase/auth');
          const { fetchGetUser, fetchCreateUser, fetchUpdateEmail } =
            await import('@/services/noteGoApi/noteGoApiComponents');
          const token = await result.user.getIdToken();
          await handleUserTokenStorage(result);

          // Check/create user in backend
          try {
            await fetchGetUser({ headers: { authorization: `Bearer ${token}` } });
          } catch (error: unknown) {
            if (isUserNotFoundError(error)) {
              try {
                await fetchCreateUser({
                  headers: { authorization: `Bearer ${token}` },
                  body: {
                    first_name: result?.user?.displayName?.split(' ')[0] || '',
                    // Everything after the first token is the last name — a bare
                    // [1] drops the rest of multi-word surnames ("Van Der Berg").
                    last_name: result?.user?.displayName?.split(' ').slice(1).join(' ') || '',
                    phone_number: '',
                    firebase_uid: result.user.uid, // Pass Firebase UID to prevent duplicate user documents
                    // Include default subscription to prevent AI processing failures
                    // Backend requires subscription.plan to be set for AI worker to process media
                    subscription: DEFAULT_FREE_SUBSCRIPTION,
                  },
                });

                // Update the email for redirect sign-in users as well
                if (result.user.email) {
                  try {
                    await fetchUpdateEmail({
                      headers: { authorization: `Bearer ${token}` },
                      body: {
                        email: result.user.email,
                      },
                    });
                  } catch (emailError) {
                    errorReporter.expected('Failed to update email in backend during redirect', emailError, {
                      feature: 'auth',
                      operation: 'updateEmailRedirect',
                    });
                  }
                }
              } catch (createError) {
                errorReporter.critical('Failed to create user in backend during redirect', createError, {
                  feature: 'auth',
                  operation: 'createUserRedirect',
                  firebaseUid: result.user.uid,
                });
              }
            }
          }
        }
      } catch (error) {
        errorReporter.critical('Error handling Google OAuth redirect result', error, {
          feature: 'auth',
          operation: 'handleRedirectResult',
        });
      }
    };

    checkRedirectResult();
  }, []);

  // Silent auto-login: try extension auth automatically on mount
  useEffect(() => {
    // Only attempt once per page load
    if (silentLoginAttemptedRef.current) return;
    // CRITICAL: Check logout flag synchronously BEFORE any async operations
    // This is a defense against race condition where extension auth arrives before
    // the logout useEffect has a chance to set skipExtensionAuth
    // Checks the durable sessionStorage flag AND the cross-tab timestamp — a
    // failed auth.signOut() leaves the timestamp to expire while logout is still
    // pending, which would let silent auto-login through (CONTRACT #1 Part 3).
    if (isLogoutInProgress()) {
      logger.debug('auth', 'Skipping silent login - logout was requested');
      silentLoginAttemptedRef.current = true;
      return;
    }
    // Skip if conditions aren't right
    if (!extensionHasAuth || skipExtensionAuth || userLoggedIn || loading) return;

    silentLoginAttemptedRef.current = true;

    const attemptSilentLogin = async () => {
      const success = await signInWithExtension();
      if (!success) {
        // Silently fall back to normal login - no error shown
        setSkipExtensionAuth(true);
      }
      // If success, userLoggedIn will become true and redirect happens via other useEffect
    };

    attemptSilentLogin();
  }, [extensionHasAuth, skipExtensionAuth, userLoggedIn, loading, signInWithExtension]);

  useEffect(() => {
    if (!loading && userLoggedIn) {
      // CRITICAL: Don't redirect if logout is in progress - doSignOut() hasn't completed yet.
      // Without this guard, the redirect effect fires before signOut completes (because
      // userLoggedIn is still true), clears all logout flags, and navigates back to the
      // protected page. Then auth.signOut() eventually fires, the user is kicked to login
      // again but without logout flags, and the extension auto-login re-authenticates them.
      if (sessionStorage.getItem('userRequestedLogout') === 'true') {
        return;
      }
      // Clear both logout flags when user successfully logs in
      // sessionStorage flag is used by the guard above and checked in authStore.ts
      sessionStorage.removeItem('userRequestedLogout');
      clearLogoutFlag();
      const storedRedirectUrl = sessionStorage.getItem('redirectAfterLogin');
      // It's crucial to remove the item immediately after reading it
      // to prevent it from being used on subsequent logins.
      if (storedRedirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(storedRedirectUrl);
      } else {
        // Fallback to noteId from URL or home page
        const redirectUrl = noteId ? `${RouteMap.NOTE}/${noteId}` : RouteMap.HOME;
        navigate(redirectUrl);
      }
    }
  }, [loading, userLoggedIn, navigate, noteId]);

  useEffect(() => {
    if (action === 'login') {
      setShowLogin(true);
    } else if (action === 'signup') {
      setShowLogin(false);
    }
  }, [action]);

  const renderForm = () => {
    if (showForgotPassword) {
      return <ForgotPassword setShowForgotPassword={setShowForgotPassword} />;
    }
    if (showLogin) {
      return (
        <LoginForm
          setShowLogin={setShowLogin}
          setShowForgotPassword={setShowForgotPassword}
          redirectedFromSharedNote={redirectedFromSharedNote}
          showLogin={showLogin}
        />
      );
    }
    return (
      <SignUpForm
        setShowLogin={setShowLogin}
        redirectedFromSharedNote={redirectedFromSharedNote}
        showLogin={showLogin}
      />
    );
  };

  return (
    <>
      <LoginNavbar />
      <Toolbar />

      <Stack
        alignItems="center"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        height={'calc(100vh - 64px)'}
        px={2}
        pt={{ xs: 0, md: 12 }}
        spacing={2}
      >
        {renderForm()}
      </Stack>
    </>
  );
};

export default Login;
